type ParentRow = { id?: string | null; parentId?: string | null };

/** Split a flat selection list into roots and children. A parent that is not in the list is dangling. */
export const groupSelectionsByParent = <T extends ParentRow>(rows: readonly T[]) => {
    const byId = new Map<string, T>();
    for (const row of rows) {
        if (row.id) byId.set(row.id, row);
    }
    const childrenOf = new Map<string, T[]>();
    const roots: T[] = [];
    const dangling: T[] = [];
    for (const row of rows) {
        const parentId = row.parentId;
        if (parentId && parentId !== row.id && byId.has(parentId)) {
            const list = childrenOf.get(parentId) ?? [];
            list.push(row);
            childrenOf.set(parentId, list);
        } else if (parentId) {
            dangling.push(row);
        } else {
            roots.push(row);
        }
    }
    return { roots, childrenOf, dangling };
};

export type SelectionNode<T> = { row: T; children: SelectionNode<T>[] };

/**
 * Parents before children, up to `maxDepth` levels. A negative line does not
 * keep its children. A cycle is not a root, so it is left out rather than looped.
 */
export const nestSelections = <T extends ParentRow & { quantity?: string | number | null }>(
    rows: readonly T[],
    maxDepth: number,
): SelectionNode<T>[] => {
    const grouped = groupSelectionsByParent(rows);
    const seen = new Set<T>();
    const walk = (nodes: readonly T[], depth: number): SelectionNode<T>[] => {
        if (depth >= maxDepth) return [];
        const out: SelectionNode<T>[] = [];
        for (const row of nodes) {
            if (seen.has(row)) continue;
            seen.add(row);
            const quantity = Number(row.quantity ?? 0);
            const children =
                quantity < 0 || !row.id ? [] : walk(grouped.childrenOf.get(row.id) ?? [], depth + 1);
            out.push({ row, children });
        }
        return out;
    };
    const topLevel = new Set<T>([...grouped.roots, ...grouped.dangling]);
    return walk(
        rows.filter((row) => topLevel.has(row)),
        0,
    );
};
